/**
 * Homme Paradis — behaviour layer.
 * 1. [data-hp-buy] forms: in-place add to bag that drives Savor's own cart drawer + bubble
 *    via the CartLinesUpdateEvent contract (same as assets/product-form.js).
 * 2. [data-hp-reveal]: reveal-on-scroll.
 * Every feature degrades to plain HTML: forms still post to /cart/add without JS.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/* ---------- 1. Buy forms ---------- */

const ADDED_RESET_MS = 2400;

/** Cart sections the drawer/cart page want re-rendered, same lookup as product-form.js */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

async function fetchCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems && 'fetchCartData' in cartItems) {
    await customElements.whenDefined('cart-items-component');
    return /** @type {any} */ (cartItems).fetchCartData();
  }
  const res = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  return res.json();
}

/** @param {HTMLFormElement} form */
function initBuyForm(form) {
  form.noValidate = true; // we validate ourselves with a friendlier message
  const button = /** @type {HTMLButtonElement} */ (form.querySelector('button[type="submit"]'));
  const label = button?.querySelector('.hp-btn__label');
  const msg = form.querySelector('.hp-buy__msg');
  const defaultLabel = label?.textContent ?? '';
  let resetTimer = 0;

  const say = (text, isError = false) => {
    if (!msg) return;
    msg.textContent = text;
    msg.classList.toggle('is-error', isError);
  };

  form.addEventListener('change', () => {
    form.classList.remove('is-invalid');
    say('');
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!button || button.disabled) return;

    const formData = new FormData(form);
    const variantId = formData.get('id');
    if (!variantId) {
      form.classList.remove('is-invalid');
      void form.offsetWidth; // restart the shake animation
      form.classList.add('is-invalid');
      say('Pick your size first.', true);
      /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]:not(:disabled)'))?.focus();
      return;
    }

    const sections = cartSectionIds();
    if (sections) formData.append('sections', sections);

    clearTimeout(resetTimer);
    button.classList.remove('is-added');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = 'Adding';
    say('');

    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const baseDetail = { source: 'hp-buy-form', sourceId: form.id, itemCount: 1, productId: form.dataset.productId };

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      }).then((r) => r.json());

      const ajaxCart = await fetchCart();
      const cart = CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart);

      if (response.status) {
        deferred.resolve({ cart, detail: { ...baseDetail, didError: true, items: ajaxCart.items } });
        throw new Error(response.description || response.message || 'Could not add to bag.');
      }

      deferred.resolve({
        cart,
        detail: { ...baseDetail, didError: false, items: ajaxCart.items, sections: response.sections },
      });

      button.classList.add('is-added');
      if (label) label.textContent = 'Added to bag';
      say('Added to your bag.');
      resetTimer = window.setTimeout(() => {
        button.classList.remove('is-added');
        if (label) label.textContent = defaultLabel;
        say('');
      }, ADDED_RESET_MS);
    } catch (error) {
      if (label) label.textContent = defaultLabel;
      say(error instanceof Error ? error.message : 'Something went wrong. Try again.', true);
      deferred.reject?.(error);
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  });
}

/* ---------- 2. Reveal on scroll ---------- */

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-in');
            revealObserver?.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
      )
    : null;

/* ---------- Boot (also re-runs when the theme editor re-renders a section) ---------- */

/** @param {ParentNode} root */
function boot(root = document) {
  root.querySelectorAll('form[data-hp-buy]:not([data-hp-ready])').forEach((form) => {
    form.setAttribute('data-hp-ready', '');
    initBuyForm(/** @type {HTMLFormElement} */ (form));
  });
  root.querySelectorAll('[data-hp-reveal]:not(.is-in), [data-hp-inview]:not(.is-in)').forEach((el) => {
    if (revealObserver) revealObserver.observe(el);
    else el.classList.add('is-in');
  });
}

/* ---------- 3. Club pop-up (sections/hp-popup.liquid) ---------- */

const CLUB_KEY = 'hp-club';
const DAY_MS = 86_400_000;

/** @returns {{ joined?: boolean, until?: number } | null} */
function clubState() {
  try {
    return JSON.parse(localStorage.getItem(CLUB_KEY) || 'null');
  } catch {
    return null;
  }
}

/** @param {{ joined?: boolean, until?: number }} value */
function saveClubState(value) {
  try {
    localStorage.setItem(CLUB_KEY, JSON.stringify(value));
  } catch {
    /* storage blocked: pop-up simply shows again next visit */
  }
}

/** @param {HTMLDialogElement} dialog */
function initClubPopup(dialog) {
  const snoozeDays = Number(dialog.dataset.snoozeDays) || 14;
  const justPosted = location.hash === '#hp-club-form';

  const open = () => {
    if (dialog.open || document.querySelector('dialog[open]')) return; // never stack on the cart drawer
    dialog.showModal();
    dialog.focus(); // focus the sheet itself: no ring on close, no keyboard popping up on phones
  };

  dialog.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target === dialog || target.closest('[data-hp-pop-close]')) dialog.close();
  });

  dialog.addEventListener('close', () => {
    const joined = justPosted || Boolean(dialog.querySelector('[data-hp-pop-joined]'));
    saveClubState(joined ? { joined: true } : { until: Date.now() + snoozeDays * DAY_MS });
    if (justPosted) history.replaceState(null, '', location.pathname + location.search);
  });

  // After a sign-up the page reloads at #hp-club-form: reopen to show the confirmation
  if (justPosted) {
    open();
    return;
  }

  if (dialog.hasAttribute('data-design-mode')) return; // editor opens it via section select
  const state = clubState();
  if (state?.joined || (state?.until && state.until > Date.now())) return;

  window.setTimeout(open, (Number(dialog.dataset.delay) || 7) * 1000);
}

document.querySelectorAll('dialog[data-hp-pop]').forEach((d) => initClubPopup(/** @type {HTMLDialogElement} */ (d)));
document.addEventListener('shopify:section:select', (e) => {
  const dialog = /** @type {HTMLElement} */ (e.target).querySelector('dialog[data-hp-pop]');
  if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
});
document.addEventListener('shopify:section:deselect', (e) => {
  /** @type {HTMLElement} */ (e.target).querySelector('dialog[data-hp-pop]')?.close();
});

/* ---------- 4. Full-screen menu (sections/hp-menu.liquid) ---------- */

function initMenu() {
  const menu = document.querySelector('dialog[data-hp-menu]');
  if (!(menu instanceof HTMLDialogElement)) return; // triggers keep their no-JS fallback link
  const preview = menu.querySelector('.hp-menu__preview');
  const previewImg = /** @type {HTMLImageElement | null} */ (menu.querySelector('.hp-menu__preview-img'));
  const caption = menu.querySelector('.hp-menu__caption');
  const triggers = () => document.querySelectorAll('[data-hp-menu-open]');
  let swapTimer = 0;

  const setExpanded = (value) => triggers().forEach((t) => t.setAttribute('aria-expanded', String(value)));

  document.addEventListener('click', (event) => {
    const trigger = /** @type {HTMLElement} */ (event.target).closest?.('[data-hp-menu-open]');
    if (!trigger) return;
    event.preventDefault();
    if (menu.open) return;
    menu.showModal();
    menu.focus();
    setExpanded(true);
  });

  menu.addEventListener('close', () => setExpanded(false));

  menu.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-hp-menu-close]')) return menu.close();
    const link = target.closest('a');
    if (!link) return;
    const href = link.getAttribute('href') || '';
    if (href === '#hp-club') return menu.close(); // the document-level handler opens the pop-up
    // Same-page anchors (e.g. /#drop on the homepage): close first so the page can scroll
    const url = new URL(link.href, location.href);
    if (url.pathname === location.pathname && url.hash) menu.close();
  });

  // Hover preview swap (desktop): the next image is fully decoded before it replaces the
  // current one, so the panel never flashes empty.
  const show = (link) => {
    const url = link.dataset.preview;
    if (!preview || !previewImg || !url || previewImg.getAttribute('src') === url) return;
    clearTimeout(swapTimer);
    const next = new Image();
    next.src = url;
    next
      .decode()
      .catch(() => {})
      .then(() => {
        preview.classList.add('is-swapping');
        swapTimer = window.setTimeout(() => {
          previewImg.src = url;
          preview.setAttribute('data-kind', link.dataset.previewKind || 'cutout');
          if (caption) caption.textContent = link.dataset.caption || '';
          preview.classList.remove('is-swapping');
        }, 160);
      });
  };
  menu.querySelectorAll('.hp-menu__link').forEach((link) => {
    link.addEventListener('mouseenter', () => show(link));
    link.addEventListener('focus', () => show(link));
  });
}

/* Warm images inside closed dialogs (menu, club pop-up) once the page is idle,
   so nothing opens with an empty frame. */
function warmDialogImages() {
  document.querySelectorAll('dialog img[loading="lazy"]').forEach((img) => img.setAttribute('loading', 'eager'));
  document.querySelectorAll('.hp-menu__link[data-preview]').forEach((link) => {
    new Image().src = /** @type {HTMLElement} */ (link).dataset.preview || '';
  });
}
if ('requestIdleCallback' in window) requestIdleCallback(warmDialogImages, { timeout: 3000 });
else window.setTimeout(warmDialogImages, 2000);

/* Any link to #hp-club (nav strip, menu, footer) opens the club pop-up instead of jumping */
document.addEventListener('click', (event) => {
  const link = /** @type {HTMLElement} */ (event.target).closest?.('a[href="#hp-club"]');
  if (!link) return;
  const club = document.querySelector('dialog[data-hp-pop]');
  if (!(club instanceof HTMLDialogElement)) return;
  event.preventDefault();
  queueMicrotask(() => {
    if (club.open) return;
    club.showModal();
    club.focus();
  });
});

initMenu();
boot();
document.addEventListener('shopify:section:load', (e) => boot(/** @type {HTMLElement} */ (e.target)));
